/**
 * BackgroundUploadIndicator — the persistent card for "upload em segundo
 * plano" (interview video uploads started from `evaluations/new.tsx`).
 *
 * Mounted ONCE, at the top of the app (see `App.tsx`), as a sibling of
 * `<Routes>` — never inside a route element, so it keeps rendering no matter
 * where the person navigates to right after submitting. It reads its list of
 * tasks straight from `useBackgroundUploadStore` (a Zustand module singleton,
 * `@/stores/background-upload-store`), which is what actually survives the
 * create-evaluation screen unmounting.
 *
 * Also owns the `beforeunload` guard: the store keeps NO persistence (an
 * explicit, owner-accepted limit — closing/reloading the tab loses whatever
 * is in flight), so the one thing this component can still do is make that
 * cost impossible to hit by accident. The guard is scoped to tasks that are
 * NOT YET `done`/`error` — once a task resolves there is nothing left to lose
 * by closing the tab.
 *
 * Visual vocabulary follows `UploadArea` (the file card inside the form this
 * indicator replaces the wait for) no que é conteúdo: `bg-sunk`/`bg-info`
 * progress track+fill — same tokens, same shape, because this IS the
 * continuation of that same upload, just relocated.
 *
 * A SUPERFÍCIE, essa, é `glass-float` e não o painel opaco de origem. Este
 * cartão é chrome FLUTUANTE — vive `fixed` no canto, por cima de tudo o resto,
 * exactamente como um toast ou um menu. Opaco, tapava a tela por baixo no
 * momento em que a pessoa está a trabalhar nela; em vidro, deixa-a ver-se. E é
 * UM elemento (raramente dois), nunca uma lista longa — que é a única condição
 * em que o `globals.css` autoriza `backdrop-filter`.
 *
 * A `glass-float` é auto-contida: traz tinta, lustro, blur, borda, aresta e
 * sombra. Não juntar `border`/`shadow-*` aqui — nem `backdrop-blur-*`, que
 * colide com o blur da própria utilidade (ver o aviso no `globals.css`).
 */
import { type ReactElement } from 'react'
import { Link } from 'react-router-dom'
import { Loader2, CheckCircle2, AlertTriangle, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useBeforeUnloadGuard } from '@/hooks/use-before-unload-guard'
import {
  useBackgroundUploadStore,
  type BackgroundUploadTask,
} from '@/stores/background-upload-store'

const ACTIVE_PHASES = new Set<BackgroundUploadTask['phase']>([
  'uploading-video',
  'uploading-transcript',
  'creating',
])

function isActive(task: BackgroundUploadTask): boolean {
  return ACTIVE_PHASES.has(task.phase)
}

function phaseLabel(task: BackgroundUploadTask): string {
  switch (task.phase) {
    case 'uploading-video':
      return `Enviando vídeo… ${Math.round(task.progress * 100)}%`
    case 'uploading-transcript':
      return 'Enviando transcrição…'
    case 'creating':
      return 'Criando avaliação…'
    case 'done':
      return 'Avaliação criada'
    case 'error':
      return 'Falha no envio'
  }
}

function UploadTaskCard({ task }: { task: BackgroundUploadTask }): ReactElement {
  const { cancelTask, dismissTask } = useBackgroundUploadStore.getState()
  const active = isActive(task)
  const pct = Math.round(task.progress * 100)

  return (
    <div className="glass-float rise w-80 rounded-md px-4 py-3">
      <div className="flex items-start gap-2.5">
        {task.phase === 'error' ? (
          <AlertTriangle className="text-error mt-0.5 size-4 shrink-0" aria-hidden="true" />
        ) : task.phase === 'done' ? (
          <CheckCircle2 className="text-success mt-0.5 size-4 shrink-0" aria-hidden="true" />
        ) : (
          <Loader2 className="text-ink-2 mt-0.5 size-4 shrink-0 animate-spin" aria-hidden="true" />
        )}
        <div className="min-w-0 flex-1">
          <p className="text-ink truncate text-sm font-medium">{task.fileName}</p>
          <p className={task.phase === 'error' ? 'text-error text-xs' : 'text-ink-2 text-xs'}>
            {task.phase === 'error' ? task.error : phaseLabel(task)}
          </p>

          {task.phase === 'uploading-video' ? (
            <div
              className="bg-sunk mt-2 h-1.5 w-full overflow-hidden rounded-full"
              role="progressbar"
              aria-valuenow={pct}
              aria-valuemin={0}
              aria-valuemax={100}
            >
              {/* `info`: um upload EM CURSO não é o mérito de ninguém. */}
              <div className="bg-info h-full transition-[width]" style={{ width: `${pct}%` }} />
            </div>
          ) : null}

          {active ? (
            <p className="text-ink-3 mt-1.5 text-xs">
              Não feche esta aba — o envio será perdido se ela fechar ou recarregar.
            </p>
          ) : null}
        </div>

        {task.phase === 'done' ? (
          <button
            type="button"
            onClick={() => dismissTask(task.id)}
            aria-label="Fechar"
            className="text-ink-2 hover:bg-sunk rounded-md p-1"
          >
            <X className="size-3.5" aria-hidden="true" />
          </button>
        ) : null}
      </div>

      <div className="mt-3 flex justify-end gap-2">
        {task.phase === 'uploading-video' || task.phase === 'uploading-transcript' ? (
          <Button type="button" variant="ghost" size="sm" onClick={() => cancelTask(task.id)}>
            Cancelar
          </Button>
        ) : null}
        {task.phase === 'error' ? (
          <Button type="button" variant="ghost" size="sm" onClick={() => dismissTask(task.id)}>
            Dispensar
          </Button>
        ) : null}
        {task.phase === 'done' && task.evaluationId ? (
          <Button type="button" variant="secondary" size="sm" asChild>
            <Link to={`/evaluations/${task.evaluationId}`} onClick={() => dismissTask(task.id)}>
              Ver avaliação
            </Link>
          </Button>
        ) : null}
      </div>
    </div>
  )
}

export function BackgroundUploadIndicator(): ReactElement | null {
  const tasks = useBackgroundUploadStore((s) => s.tasks)
  useBeforeUnloadGuard(tasks.some(isActive))

  if (tasks.length === 0) return null

  return (
    <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-2.5">
      {tasks.map((task) => (
        <UploadTaskCard key={task.id} task={task} />
      ))}
    </div>
  )
}

export default BackgroundUploadIndicator
